import React, { Dispatch, SetStateAction } from "react";
import { Box, Button } from "@mui/material";
import Badge from "@mui/material/Badge";
import Stack from "@mui/material/Stack";
import i18n from "../../i18n";
import NotificationCenterRow from "./NotificationCenterRow";
import { store, useTaxSelector } from "../../redux/general/reduxStore";
import Slide from "@mui/material/Slide";
import {
    deleteAllNotifications,
    deleteNotification,
    listNotifications,
    markAllNotificationsRead,
    updateNotification,
} from "../../backend/taxguru/client/jmf/coreNotificationClient";
import { CoreFrontendNotificationDto, CoreFrontendNotificationRequestDto, JmfPermissionEntity } from "@mm-products/java-multima-framework";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { addNotification, resetNotifications } from "../../redux/userSlice";
import TaxGuruDialog from "../../components/TaxGuruDialog";
import AuthorizedComponent from "../../permissions/AuthorizedComponent";
import { TaxPermissionBuilder } from "../../permissions/TaxGuruPermissionBuilder";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import TaxGuruIconButton from "../../components/button/TaxGuruIconButton";
import { useTranslation } from "react-i18next";
import TaxGuruTooltip from "../../components/filter/TaxGuruTooltip";
import notificationState = CoreFrontendNotificationRequestDto.notificationState;

type NotificationBellIconButtonProps = {
    setOpen: Dispatch<SetStateAction<boolean>>;
    notificationsCount: number;
    unreadNotificationsCount: number;
};
const NotificationBellIconButton = ({
    unreadNotificationsCount,
    notificationsCount,
    setOpen,
}: NotificationBellIconButtonProps) => (
    <TaxGuruIconButton sx={{ color: "secondary" }} onClick={() => setOpen(notificationsCount > 0)} size="small">
        <Badge badgeContent={unreadNotificationsCount} color="warning">
            <NotificationsNoneIcon />
        </Badge>
    </TaxGuruIconButton>
);

type ActionsProps = {
    notifications: CoreFrontendNotificationDto[];
    reloadNotifications: () => void;
};
const Actions = ({ notifications, reloadNotifications }: ActionsProps) => {
    const deleteAllNotificationsCallback = () => {
        if (notifications.length === 0) {
            return;
        }
        deleteAllNotifications(notifications.map((value) => value.id!)).then(reloadNotifications);
    };

    const markAllNotificationsReadCallback = () => {
        if (notifications.length === 0) {
            return;
        }
        markAllNotificationsRead(notifications.map((value) => value.id!)).then(reloadNotifications);
    };
    return (
        <Stack direction="row" spacing={2} justifyContent="space-between">
            <Button variant="contained" color="error" onClick={deleteAllNotificationsCallback}>
                {i18n.t("actions.delete-all")}
            </Button>
            <Button variant="contained" color="primary" onClick={markAllNotificationsReadCallback}>
                {i18n.t("actions.readAll")}
            </Button>
        </Stack>
    );
};

const NotificationCenter = () => {
    const [open, setOpen] = React.useState(false);
    const [tab, setTab] = React.useState(0);
    const { notifications } = useTaxSelector((state) => state.user);

    if (notifications.length === 0 && open) {
        setOpen(false);
    }

    const reloadNotifications = () =>
        listNotifications({ specificFilters: [{ name: "forUser", value: "true" }] }).then(({ data }) => {
            store.dispatch(resetNotifications());
            if ((data?.data?.length ?? 0) > 0) {
                store.dispatch(addNotification(data.data ?? []));
            }
        });

    React.useEffect(() => {
        // effect for initial loading of notifications
        reloadNotifications();
    }, []);

    const markRead = React.useCallback((notification: CoreFrontendNotificationDto, read: boolean) => {
        if (!notification.id) {
            return;
        }
        updateNotification(notification.id, {
            notificationState: read ? notificationState.READ : notificationState.UNREAD,
            notificationType: notification.notificationType,
            message: notification.message,
            userId: notification.user?.id,
        }).then(reloadNotifications);
    }, []);

    const onRemoveNotificationHandler = React.useCallback((notification: CoreFrontendNotificationDto) => {
        if (!notification.id) {
            return;
        }
        deleteNotification(notification.id).then(reloadNotifications);
    }, []);

    const unreadNotifications = notifications.filter((value) => value.notificationState === "UNREAD");
    const readNotifications = notifications.filter((value) => value.notificationState === "READ");

    const handleClose = () => setOpen(false);
    const { t } = useTranslation();

    return (
        <Box>
            <AuthorizedComponent
                unauthorizedMode="no-render"
                permissions={TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_FRONTEND_NOTIFICATION).build()}
            >
                {notifications.length === 0 && (
                    <TaxGuruTooltip title={t("notification.youHaveNoNotifications")} placement="bottom">
                        <Box>
                            {/* box is really needed */}
                            <NotificationBellIconButton
                                notificationsCount={notifications.length}
                                unreadNotificationsCount={unreadNotifications.length}
                                setOpen={setOpen}
                            />
                        </Box>
                    </TaxGuruTooltip>
                )}
                {notifications.length > 0 && (
                    <NotificationBellIconButton
                        notificationsCount={notifications.length}
                        unreadNotificationsCount={unreadNotifications.length}
                        setOpen={setOpen}
                    />
                )}
            </AuthorizedComponent>
            <TaxGuruDialog
                sx={{ "& .MuiPaper-root": { width: { xs: "100%", md: "700px" }, height: { xs: "100%", md: "400px" } } }}
                title={i18n.t("notification.notificationCenter")}
                TransitionComponent={Slide}
                open={open}
                onClose={handleClose}
                actions={<Actions notifications={notifications} reloadNotifications={reloadNotifications} />}
            >
                <Tabs value={tab} onChange={(e, value) => setTab(value)}>
                    <Tab value={0} label={i18n.t("headings.unreadMessages")} />
                    <Tab value={1} label={i18n.t("headings.readMessages")} />
                </Tabs>
                <Stack spacing={1} sx={{ overflowY: "auto", marginTop: 3 }}>
                    {(tab === 0 ? unreadNotifications : readNotifications).map((value) => (
                        <Slide key={value.id} direction="right" in={true}>
                            <div>
                                <NotificationCenterRow onMarkRead={markRead} notification={value} onRemoveItem={onRemoveNotificationHandler} />
                            </div>
                        </Slide>
                    ))}
                </Stack>
            </TaxGuruDialog>
        </Box>
    );
};

export default NotificationCenter;
